import { pattern, test, required } from '@angellira/form';

import { PHONE_MOBILE, PHONE_COMMERCIAL } from '../services/phone';
import { isValid as validateCPF } from '@fnando/cpf';

const exactLength = len => x => String(x).length === len;
const maxLength = len => x => x ? String(x).length <= len : true;
const minLength = len => x => x ? String(x).length >= len : true;

const numberIfExists = () => x => x && String(x).length ? /^\d+$/.test(x) : true;

const requiredRule = {
  'campo obrigatório': required(),
  'não pode ser vazio': x => !!String(x).trim().length
};

const validateDocument = () => (value) => {
  const normalized = String(value || '').replace(/[^A-Za-z0-9]/g, '');
  const isCPF = /^\d{11}$/.test(normalized);
  const isAlphanumericCNPJ = /^[A-Za-z0-9]{12}\d{2}$/.test(normalized);

  if (isCPF) return validateCPF(normalized);
  if (isAlphanumericCNPJ) return true;

  return false;
};

const valiDate = () => value => value
  ? !isNaN(new Date(value)) && dateBefore(new Date('01-01-2200'))
  : true;
const dateAfter = after => value => value ? new Date(value) > after : true;
const dateBefore = before => value => value ? new Date(value) < before : true;

const integrationString = {
  'não deve possuir acentos': x => !/[\u0300-\u036f]/g.test(x.normalize('NFD')),
  'não deve possuir caracteres especiais': x => !/[^A-Za-z0-9 ]+/g.test(x),
  'espaçamento inválido no preenchimento': x => !/\s{2,}/g.test(x),
  'não deve começar ou terminar com espaço': x => !/^\s+|\s+$/g.test(x),
};

const baseDateValidations = {
  'deve estar completo': required(),
  'deve ser uma data válida': valiDate(),
};

const phoneMobile = {
  'deve possuir pelo menos um telefone móvel informado': arr => arr.some(
    x => String(x.typeId) === String(PHONE_MOBILE) && x.phone.length
  )
};

const phoneCommercial = {
  'deve possuir pelo menos um telefone comercial informado': arr => arr.some(
    x => String(x.typeId) === String(PHONE_COMMERCIAL) && x.phone.length
  )
};

const phone = {
  'os telefones informados devem estar completos': arr => arr.every(
    x => x.phone.length ? /^\(\d{2}\)\s*\d{4,5}-?\d{4}$/g.test(x.phone) : true
  )
};

export const requiredString = pattern({
  ...requiredRule,
  ...integrationString,
});

export const name = pattern({
  ...requiredRule,
  ...integrationString,
  'deve conter apenas letras e espaços': x => /^[a-zA-Z ]+$/g.test(x.trim()),
  'insira o nome completo': x => /\s/.test(x.trim())
});

export const legalPerson = pattern({
  ...requiredRule,
  ...integrationString,
  'deve possuir no máximo 61 dígitos': maxLength(61)
});

export const naturalPerson = pattern({
  ...requiredRule,
  ...integrationString,
  'deve conter apenas letras e espaços': x => /^[a-zA-Z ]+$/g.test(x.trim()),
  'insira o nome completo': x => /\s/.test(x.trim()),
  'deve possuir no máximo 61 dígitos': maxLength(61) 
});

export const document = pattern({
  ...requiredRule,
  'deve ser um documento válido': validateDocument(),
});

export const cnh = pattern({
  ...requiredRule,
  ...integrationString,
  'deve ser um número de CNH válido com 11 dígitos': x => String(x).length === 11
});

export const birth = pattern({
  ...baseDateValidations,
  'deve ser uma data anterior à hoje': dateBefore(new Date()),
  'deve ser uma data após o ano 1900': dateAfter(new Date('1900-01-01')),
});

export const validateCertificate = pattern({
  ...baseDateValidations,
  'deve ser maior que a data atual': dateAfter(new Date()),
});

export const lastLicensing = required => pattern(Object.assign(
  {
    'deve ser uma data válida': valiDate(),
    'deve ser uma data anterior à hoje': dateBefore(new Date().setDate(new Date().getDate() -1)),
    'deve ser uma data após o ano 1900': dateAfter(new Date('1900-01-01')),
  },
  required ? requiredRule : {}
));

export const requiredDate = pattern(baseDateValidations);

export const cnhDate = pattern({
  ...baseDateValidations,
  'deve ser maior que a data atual': dateAfter(new Date()),
});

export const requiredOnly = pattern(requiredRule);

export const plate = pattern({
  ...requiredRule,
  'deve ter no máximo 8 caracteres': x => String(x).length <= 8,
  'deve ser uma placa válida': test(
    /^[a-zA-Z]{3}-[0-9]{3,4}$|^(?=(?:.*[0-9]){3})(?=(?:.*[a-zA-Z]){4})[a-zA-Z0-9]{7}$/
  )
});

export const owner = pattern({
  'você deve selecionar um proprietário': required()
});

export const year = pattern({
  ...requiredRule,
  'deve ser um número': v => /\d+/.test(String(v))
});

export const axles = pattern({
  ...requiredRule,
  'deve ser maior ou igual a 2': x => parseInt(x) >= 2,
  'deve possuir no máximo 4 dígitos': maxLength(4)
});

export const naturalPersonPhone = pattern({
  ...phone,
  ...phoneMobile
});

export const legalPersonPhone = pattern({
  ...phone,
  ...phoneCommercial
});

export const antt = required => pattern(Object.assign(
  {
    'deve possuir no mínimo 9 caracteres': minLength(9),
    'deve possuir no máximo 10 caracteres': maxLength(10)
  },
  required ? requiredRule : {}
));

export const cep = pattern({
  'deve ser um número de CEP': test(/^\d{5}-\d{3}$/)
});

export const number = pattern({
  'deve ser um número': numberIfExists()
});

export const cnhSecurity = pattern({
  ...requiredRule,
  ...integrationString,
  'deve ser um número': numberIfExists(),
  'deve possuir exatamente 11 dígitos': exactLength(11),
});

export const renavam = pattern({
  ...requiredRule,
  ...integrationString,
  'deve possuir no máximo 11 dígitos': maxLength(11)
});

export const chassis = pattern({
  ...requiredRule,
  ...integrationString,
  'deve possuir no minimo 5 caracteres': minLength(5),
  'deve possuir no máximo 21 caracteres': maxLength(21)
});

export const fleet = pattern({
  'deve ser um número': numberIfExists()
});

export const oneOf = options => pattern({
  'deve ser um valor selecionado na lista': x => options.map(String).includes(String(x))
});

export const fileMimes = (mimes, required = false) => pattern({
  'este campo é obrigatório': (_v, _o, ev) => required
    ? !!ev.target && !!ev.target.files.length
    : true,
  [`deve ser um arquivo do tipo ${mimes.join(' ou ')}`]: (_v, _o, ev) => Array.from(ev.target.files)
    .every(f => mimes.includes(f.type)),
  'o arquivo não deve exceder 2MB de espaço de armazenamento': (_v, _o, ev) => Array
    .from(ev.target.files)
    .every(f => f.size < 2 * 1024 * 1024),
});
